import { c as _c } from "react/compiler-runtime";
import type { FontVariant } from '@onefootprint/design-tokens';
import { cx } from 'class-variance-authority';
import type React from 'react';
import type { InputHTMLAttributes } from 'react';
import { forwardRef, useEffect, useId } from 'react';
import mergeRefs from 'react-merge-refs';
import { useFormattedValues } from '../../../hooks/use-formatted-values';
import { useInputMask } from '../../../hooks/use-input-mask';
import type { MaskConfig } from '../../../utils/mask-utils';
import Hint from '../../hint';
import Label from '../../label';
import type { FieldProps } from '../field';
export type InternalInputProps = {
  prefixComponent?: React.ReactNode;
  suffixComponent?: React.ReactNode;
  fontVariant?: FontVariant;
};
type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;
export type InputProps = {
  value?: string;
  mask?: MaskConfig;
  containerClassName?: string;
} & NativeInputProps & FieldProps;
type AllInputProps = InputProps & InternalInputProps;
const BaseInput = forwardRef<HTMLInputElement, AllInputProps>((t0, ref) => {
  const $ = _c(84);
  let baseID;
  let className;
  let containerClassName;
  let defaultValue;
  let hint;
  let label;
  let mask;
  let onChange;
  let onChangeText;
  let placeholder;
  let prefixComponent;
  let props;
  let required;
  let suffixComponent;
  let t1;
  let t2;
  let t3;
  let t4;
  let testID;
  let value;
  let width;
  if ($[0] !== t0) {
    ({
      className,
      containerClassName,
      disabled: t1,
      hasError: t2,
      hasFocus: t3,
      hint,
      id: baseID,
      label,
      mask,
      onChange,
      onChangeText,
      placeholder,
      prefixComponent,
      required,
      size: t4,
      suffixComponent,
      width,
      testID,
      value,
      defaultValue,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = baseID;
    $[2] = className;
    $[3] = containerClassName;
    $[4] = defaultValue;
    $[5] = hint;
    $[6] = label;
    $[7] = mask;
    $[8] = onChange;
    $[9] = onChangeText;
    $[10] = placeholder;
    $[11] = prefixComponent;
    $[12] = props;
    $[13] = required;
    $[14] = suffixComponent;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = testID;
    $[20] = value;
    $[21] = width;
  } else {
    baseID = $[1];
    className = $[2];
    containerClassName = $[3];
    defaultValue = $[4];
    hint = $[5];
    label = $[6];
    mask = $[7];
    onChange = $[8];
    onChangeText = $[9];
    placeholder = $[10];
    prefixComponent = $[11];
    props = $[12];
    required = $[13];
    suffixComponent = $[14];
    t1 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    testID = $[19];
    value = $[20];
    width = $[21];
  }
  const disabled = t1 === undefined ? false : t1;
  const hasError = t2 === undefined ? false : t2;
  const hasFocus = t3 === undefined ? false : t3;
  const size = t4 === undefined ? "default" : t4;
  const internalId = useId();
  const id = baseID || internalId;
  const {
    inputRef,
    handleChange: handleMaskChange,
    formatValue
  } = useInputMask(mask);
  const {
    formattedValue,
    formattedDefaultValue
  } = useFormattedValues(value as string, defaultValue as string, mask);
  let t5;
  if ($[22] !== handleMaskChange || $[23] !== onChange || $[24] !== onChangeText) {
    t5 = event => {
      handleMaskChange(event);
      if (onChange) {
        onChange(event);
      }
      if (onChangeText) {
        onChangeText(event.currentTarget.value);
      }
    };
    $[22] = handleMaskChange;
    $[23] = onChange;
    $[24] = onChangeText;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const handleChange = t5;
  let t6;
  if ($[26] !== formatValue || $[27] !== inputRef || $[28] !== mask) {
    t6 = () => {
      if (mask && inputRef.current) {
        const value_0 = inputRef.current.value;
        inputRef.current.value = formatValue(value_0 as string);
      }
    };
    $[26] = formatValue;
    $[27] = inputRef;
    $[28] = mask;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== formatValue || $[31] !== mask) {
    t7 = [mask, formatValue];
    $[30] = formatValue;
    $[31] = mask;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  useEffect(t6, t7);
  let t8;
  if ($[33] !== containerClassName) {
    t8 = cx("fp-input-container", containerClassName);
    $[33] = containerClassName;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== hasError || $[36] !== id || $[37] !== label || $[38] !== size) {
    t9 = label && <Label hasError={hasError} size={size} htmlFor={id}>{label}</Label>;
    $[35] = hasError;
    $[36] = id;
    $[37] = label;
    $[38] = size;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const t10 = width || "inherit";
  let t11;
  if ($[40] !== t10) {
    t11 = {
      width: t10
    };
    $[40] = t10;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== prefixComponent) {
    t12 = prefixComponent && <div className="absolute top-0 left-0 h-full flex items-center pl-3">{prefixComponent}</div>;
    $[42] = prefixComponent;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const t13 = suffixComponent ? "pr-6!" : size === "compact" ? "pr-3" : "pr-4";
  const t14 = prefixComponent ? "pl-9!" : size === "compact" ? "pl-3" : "pl-4";
  let t15;
  if ($[44] !== className || $[45] !== t13 || $[46] !== t14) {
    t15 = cx("fp-input fp-custom-appearance", t13, t14, className);
    $[44] = className;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const t16 = disabled ? undefined : placeholder;
  let t17;
  if ($[48] !== inputRef || $[49] !== ref) {
    t17 = mergeRefs([ref, inputRef]);
    $[48] = inputRef;
    $[49] = ref;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== disabled || $[52] !== formattedDefaultValue || $[53] !== formattedValue || $[54] !== handleChange || $[55] !== hasError || $[56] !== hasFocus || $[57] !== id || $[58] !== props || $[59] !== required || $[60] !== size || $[61] !== t15 || $[62] !== t16 || $[63] !== t17 || $[64] !== testID) {
    t18 = <input {...props} aria-required={required} className={t15} data-has-error={hasError} data-has-focus={hasFocus} data-size={size} data-testid={testID} disabled={disabled} id={id} onChange={handleChange} placeholder={t16} required={required} tabIndex={0} value={formattedValue} defaultValue={formattedDefaultValue} ref={t17} />;
    $[51] = disabled;
    $[52] = formattedDefaultValue;
    $[53] = formattedValue;
    $[54] = handleChange;
    $[55] = hasError;
    $[56] = hasFocus;
    $[57] = id;
    $[58] = props;
    $[59] = required;
    $[60] = size;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = testID;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== suffixComponent) {
    t19 = suffixComponent && <div className="absolute top-0 right-0 h-full flex items-center pr-2">{suffixComponent}</div>;
    $[66] = suffixComponent;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t11 || $[69] !== t12 || $[70] !== t18 || $[71] !== t19) {
    t20 = <div className="relative" style={t11}>{t12}{t18}{t19}</div>;
    $[68] = t11;
    $[69] = t12;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== hasError || $[74] !== hint) {
    t21 = hint && <Hint hasError={hasError}>{hint}</Hint>;
    $[73] = hasError;
    $[74] = hint;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== disabled || $[77] !== hasError || $[78] !== size || $[79] !== t20 || $[80] !== t21 || $[81] !== t8 || $[82] !== t9) {
    t22 = <div className={t8} data-has-error={hasError} data-size={size} data-disabled={disabled}>{t9}{t20}{t21}</div>;
    $[76] = disabled;
    $[77] = hasError;
    $[78] = size;
    $[79] = t20;
    $[80] = t21;
    $[81] = t8;
    $[82] = t9;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  return t22;
});
export default BaseInput;